import type { DayCount } from '../schemas/project.js';

/**
 * Periodic interest rate for a monthly amortization schedule.
 *
 * - `30/360`: every month is 30 days, year is 360 → rate = annual / 12. Constant.
 * - `actual/actual`: per-period rate scales with the calendar days between
 *   the previous due date and this one, divided by 365 (or 366 in a leap year
 *   on the year-span basis). For Phase 1 monthly schedules this is sufficient;
 *   ISDA-style splits across year boundaries are deferred.
 *
 * Both methods take an annual rate as a `number` and return the periodic rate
 * as a `number`.
 */

export function monthlyRate30_360(annualRate: number): number {
  return annualRate / 12;
}

/**
 * Calendar-aware period rate. `prevDate` and `dueDate` are ISO YYYY-MM-DD.
 * The period spans `(prevDate, dueDate]`.
 */
export function periodRateActualActual(
  annualRate: number,
  prevDate: string,
  dueDate: string,
): number {
  const days = daysBetween(prevDate, dueDate);
  const yearBasis = isLeapSpan(prevDate, dueDate) ? 366 : 365;
  return (annualRate * days) / yearBasis;
}

export function periodRate(
  method: DayCount,
  annualRate: number,
  prevDate: string,
  dueDate: string,
): number {
  if (method === '30/360') return monthlyRate30_360(annualRate);
  return periodRateActualActual(annualRate, prevDate, dueDate);
}

/**
 * Interest-free leading months for an interest grace period, derived from the
 * 1-based `interestStartMonth`. Clamped to [0, termMonths-1] so at least one
 * month always bears interest. Returns 0 when interest starts at month 1
 * (no grace / undefined), which keeps legacy schedules byte-identical.
 */
export function graceMonths(interestStartMonth: number | undefined, termMonths: number): number {
  return Math.min(Math.max(0, Math.trunc(interestStartMonth ?? 1) - 1), termMonths - 1);
}

function parseDateOnly(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!));
}

function daysBetween(from: string, to: string): number {
  const a = parseDateOnly(from).getTime();
  const b = parseDateOnly(to).getTime();
  return Math.round((b - a) / 86_400_000);
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function isLeapSpan(prev: string, due: string): boolean {
  const py = Number(prev.slice(0, 4));
  const dy = Number(due.slice(0, 4));
  for (let y = py; y <= dy; y++) if (isLeapYear(y)) return true;
  return false;
}

/**
 * Add `n` months to a YYYY-MM-DD date, clamping the day to the last valid
 * day of the target month (e.g., Jan 31 + 1 month = Feb 28/29).
 */
export function addMonths(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const target = new Date(Date.UTC(y!, m! - 1 + n, 1));
  const ty = target.getUTCFullYear();
  const tm = target.getUTCMonth();
  const lastDay = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
  const day = Math.min(d!, lastDay);
  return `${ty}-${String(tm + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
